import type {
  IngredientDensity,
  Unit,
  UnitDimension,
  UnitSystem,
  UnitWriteInput,
} from '@recipes/contracts';
import {
  Alert,
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Spinner,
  Tabs,
  TabsList,
  TabsTrigger,
} from '@recipes/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AdminPage } from '@/features/admin/admin-layout';
import { FormField } from '@/features/shared/form-field';
import { ResponsiveTable } from '@/features/shared/responsive-table';
import { api } from '@/lib/api';
import { describeError, toastError } from '@/lib/errors';
import { queryKeys } from '@/lib/query-keys';

const DIMENSIONS: UnitDimension[] = ['MASS', 'VOLUME', 'COUNT', 'LENGTH', 'TEMPERATURE', 'OTHER'];
const SYSTEMS: UnitSystem[] = ['METRIC', 'US', 'UNIVERSAL'];

export function AdminUnitsPage() {
  const { t } = useTranslation();
  const [tab, setTab] = useState<'units' | 'densities'>('units');
  return (
    <AdminPage section="units">
      <Tabs value={tab} onValueChange={(v) => setTab(v as 'units' | 'densities')} className="mb-4">
        <TabsList>
          <TabsTrigger value="units">{t('admin.units.units')}</TabsTrigger>
          <TabsTrigger value="densities">{t('admin.units.densities')}</TabsTrigger>
        </TabsList>
      </Tabs>
      {tab === 'units' ? <UnitsSection /> : <DensitiesSection />}
    </AdminPage>
  );
}

function UnitsSection() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<Unit | null | 'new'>(null);
  const units = useQuery({ queryKey: queryKeys.admin.units, queryFn: () => api.admin.units() });
  const toggleActive = useMutation({
    mutationFn: (u: Unit) => api.admin.updateUnit(u.id, { isActive: !u.isActive }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: queryKeys.admin.units }),
    onError: (e) => toastError(e),
  });
  return (
    <section aria-label={t('admin.units.units')}>
      <div className="mb-3 flex justify-end">
        <Button size="sm" onClick={() => setEditing('new')}>
          <Plus aria-hidden /> {t('admin.units.create')}
        </Button>
      </div>
      {units.isLoading && <Spinner label={t('app.loading')} />}
      {units.error && <Alert variant="destructive">{describeError(units.error)}</Alert>}
      {units.data && (
        <ResponsiveTable
          caption={t('admin.units.units')}
          rowKey={(u) => u.id}
          rows={units.data}
          columns={[
            {
              key: 'code',
              header: t('admin.units.code'),
              cell: (u) => <span className="font-mono text-xs">{u.code}</span>,
            },
            {
              key: 'label',
              header: t('admin.units.label'),
              cell: (u) => `${u.labels.singular.de} / ${u.labels.singular.en}`,
            },
            { key: 'dimension', header: t('admin.units.dimension'), cell: (u) => u.dimension },
            { key: 'system', header: t('admin.units.system'), cell: (u) => u.system },
            { key: 'factor', header: t('admin.units.baseFactor'), cell: (u) => u.baseFactor },
            {
              key: 'active',
              header: t('admin.units.active'),
              cell: (u) => (
                <Badge variant={u.isActive ? 'success' : 'muted'}>
                  {u.isActive ? t('common.yes') : t('common.no')}
                </Badge>
              ),
            },
            {
              key: 'actions',
              header: '',
              label: t('admin.actions'),
              cell: (u) => (
                <span className="flex justify-end gap-1">
                  <Button size="sm" variant="outline" onClick={() => setEditing(u)}>
                    {t('common.edit')}
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => toggleActive.mutate(u)}>
                    {u.isActive ? t('admin.units.deactivate') : t('admin.units.activate')}
                  </Button>
                </span>
              ),
            },
          ]}
        />
      )}
      <UnitDialog
        unit={editing === 'new' ? null : editing}
        open={editing !== null}
        onClose={() => setEditing(null)}
      />
    </section>
  );
}

function UnitDialog({
  unit,
  open,
  onClose,
}: {
  unit: Unit | null;
  open: boolean;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const empty = {
    code: '',
    dimension: 'MASS' as UnitDimension,
    system: 'METRIC' as UnitSystem,
    baseFactor: '1',
    sgDe: '',
    sgEn: '',
    plDe: '',
    plEn: '',
    abDe: '',
    abEn: '',
    aliases: '',
  };
  const [form, setForm] = useState(empty);
  useEffect(() => {
    if (!open) return;
    setForm(
      unit
        ? {
            code: unit.code,
            dimension: unit.dimension,
            system: unit.system,
            baseFactor: unit.baseFactor,
            sgDe: unit.labels.singular.de,
            sgEn: unit.labels.singular.en,
            plDe: unit.labels.plural.de,
            plEn: unit.labels.plural.en,
            abDe: unit.labels.abbreviation.de,
            abEn: unit.labels.abbreviation.en,
            aliases: unit.aliases.join(', '),
          }
        : empty,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, unit]);
  const set = (key: keyof typeof form, value: string) => setForm((f) => ({ ...f, [key]: value }));
  const body = (): UnitWriteInput => ({
    code: form.code.trim(),
    dimension: form.dimension,
    system: form.system,
    baseFactor: form.baseFactor.trim(),
    labels: {
      singular: { de: form.sgDe, en: form.sgEn },
      plural: { de: form.plDe || form.sgDe, en: form.plEn || form.sgEn },
      abbreviation: { de: form.abDe || form.code, en: form.abEn || form.code },
    },
    aliases: form.aliases
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean),
  });
  const save = useMutation({
    mutationFn: () => (unit ? api.admin.updateUnit(unit.id, body()) : api.admin.createUnit(body())),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.admin.units });
      void queryClient.invalidateQueries({ queryKey: queryKeys.units });
      onClose();
    },
    onError: (e) => toastError(e),
  });
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent closeLabel={t('common.close')} fullscreenOnMobile>
        <DialogHeader>
          <DialogTitle>{unit ? t('admin.units.edit') : t('admin.units.create')}</DialogTitle>
        </DialogHeader>
        <form
          noValidate
          className="space-y-3"
          onSubmit={(e) => {
            e.preventDefault();
            save.mutate();
          }}
        >
          <div className="grid grid-cols-2 gap-3">
            <FormField label={t('admin.units.code')} required>
              <Input
                value={form.code}
                maxLength={32}
                disabled={Boolean(unit)}
                onChange={(e) => set('code', e.target.value)}
              />
            </FormField>
            <FormField
              label={t('admin.units.baseFactor')}
              required
              hint={t('admin.units.baseFactorHint')}
            >
              <Input
                inputMode="decimal"
                value={form.baseFactor}
                onChange={(e) => set('baseFactor', e.target.value)}
              />
            </FormField>
            <div>
              <Label htmlFor="unit-dimension">{t('admin.units.dimension')}</Label>
              <Select value={form.dimension} onValueChange={(v) => set('dimension', v)}>
                <SelectTrigger id="unit-dimension" className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {DIMENSIONS.map((d) => (
                    <SelectItem key={d} value={d}>
                      {d}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="unit-system">{t('admin.units.system')}</Label>
              <Select value={form.system} onValueChange={(v) => set('system', v)}>
                <SelectTrigger id="unit-system" className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SYSTEMS.map((s) => (
                    <SelectItem key={s} value={s}>
                      {s}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <FormField label={`${t('admin.units.singular')} (de)`} required>
              <Input value={form.sgDe} onChange={(e) => set('sgDe', e.target.value)} />
            </FormField>
            <FormField label={`${t('admin.units.singular')} (en)`} required>
              <Input value={form.sgEn} onChange={(e) => set('sgEn', e.target.value)} />
            </FormField>
            <FormField label={`${t('admin.units.plural')} (de)`}>
              <Input value={form.plDe} onChange={(e) => set('plDe', e.target.value)} />
            </FormField>
            <FormField label={`${t('admin.units.plural')} (en)`}>
              <Input value={form.plEn} onChange={(e) => set('plEn', e.target.value)} />
            </FormField>
            <FormField label={`${t('admin.units.abbreviation')} (de)`}>
              <Input value={form.abDe} onChange={(e) => set('abDe', e.target.value)} />
            </FormField>
            <FormField label={`${t('admin.units.abbreviation')} (en)`}>
              <Input value={form.abEn} onChange={(e) => set('abEn', e.target.value)} />
            </FormField>
          </div>
          <FormField label={t('admin.units.aliases')} hint={t('admin.units.aliasesHint')}>
            <Input value={form.aliases} onChange={(e) => set('aliases', e.target.value)} />
          </FormField>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              {t('common.cancel')}
            </Button>
            <Button
              type="submit"
              loading={save.isPending}
              disabled={!form.code.trim() || !form.sgDe.trim() || !form.sgEn.trim()}
            >
              {t('common.save')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function DensitiesSection() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [value, setValue] = useState('');
  const [note, setNote] = useState('');
  const densities = useQuery({
    queryKey: queryKeys.admin.densities,
    queryFn: () => api.admin.densities(),
  });
  const upsert = useMutation({
    mutationFn: () =>
      api.admin.upsertDensity({
        ingredientName: name.trim(),
        gramsPerMilliliter: value.trim(),
        note: note.trim() || null,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.admin.densities });
      setName('');
      setValue('');
      setNote('');
    },
    onError: (e) => toastError(e),
  });
  const remove = useMutation({
    mutationFn: (id: string) => api.admin.deleteDensity(id),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: queryKeys.admin.densities }),
    onError: (e) => toastError(e),
  });
  const edit = (d: IngredientDensity) => {
    setName(d.ingredientName);
    setValue(d.gramsPerMilliliter);
    setNote(d.note ?? '');
  };
  return (
    <section aria-label={t('admin.units.densities')} className="space-y-4">
      <form
        noValidate
        className="grid gap-3 rounded-lg border p-3 sm:grid-cols-[1fr_8rem_1fr_auto] sm:items-end"
        onSubmit={(e) => {
          e.preventDefault();
          if (name.trim() && value.trim()) upsert.mutate();
        }}
      >
        <FormField label={t('admin.units.ingredient')} required>
          <Input value={name} maxLength={120} onChange={(e) => setName(e.target.value)} />
        </FormField>
        <FormField label={t('admin.units.gPerMl')} required>
          <Input inputMode="decimal" value={value} onChange={(e) => setValue(e.target.value)} />
        </FormField>
        <FormField label={t('admin.units.note')} optionalText={t('common.optional')}>
          <Input value={note} maxLength={200} onChange={(e) => setNote(e.target.value)} />
        </FormField>
        <Button type="submit" loading={upsert.isPending} disabled={!name.trim() || !value.trim()}>
          {t('admin.units.upsert')}
        </Button>
      </form>
      {densities.isLoading && <Spinner label={t('app.loading')} />}
      {densities.error && <Alert variant="destructive">{describeError(densities.error)}</Alert>}
      {densities.data && (
        <ResponsiveTable
          caption={t('admin.units.densities')}
          rowKey={(d) => d.id}
          rows={densities.data}
          columns={[
            { key: 'name', header: t('admin.units.ingredient'), cell: (d) => d.ingredientName },
            { key: 'value', header: t('admin.units.gPerMl'), cell: (d) => d.gramsPerMilliliter },
            { key: 'note', header: t('admin.units.note'), cell: (d) => d.note ?? '–' },
            {
              key: 'actions',
              header: '',
              label: t('admin.actions'),
              cell: (d) => (
                <span className="flex justify-end gap-1">
                  <Button size="sm" variant="outline" onClick={() => edit(d)}>
                    {t('common.edit')}
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => remove.mutate(d.id)}>
                    {t('common.delete')}
                  </Button>
                </span>
              ),
            },
          ]}
        />
      )}
    </section>
  );
}
